import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PolicyPageLayout from './PolicyPageLayout';

const TermsOfService = () => {
  const sections = [
    {
      title: "Acceptance of Terms",
      content: [
        "By accessing or using SoundFlowAI's services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using our services.",
        "We reserve the right to modify these terms at any time. Continued use of our services after changes constitutes acceptance of the new terms."
      ]
    },
    {
      title: "Service Description",
      content: [
        "SoundFlowAI provides AI-powered audio processing, enhancement, and automation services. Our platform allows users to upload, process, and download audio files using advanced machine learning algorithms."
      ],
      bullets: [
        "Audio enhancement and noise reduction",
        "Voice cloning and speech synthesis",
        "Music generation and sound effects creation",
        "Batch processing and workflow automation",
        "API access for enterprise integrations"
      ]
    },
    {
      title: "User Accounts",
      content: [
        "You must create an account to access most features of our platform. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account."
      ],
      bullets: [
        "You must be at least 18 years old to create an account",
        "You must provide accurate and complete registration information",
        "You are responsible for all activity under your account",
        "You must notify us immediately of any unauthorized access",
        "We reserve the right to suspend accounts that violate our terms"
      ]
    },
    {
      title: "Acceptable Use Policy",
      content: [
        "You agree to use our services only for lawful purposes and in accordance with these Terms. You are prohibited from using our platform for any illegal, harmful, or abusive activities."
      ],
      bullets: [
        "Do not upload content that infringes copyrights or trademarks",
        "Do not create deepfakes or synthetic media to deceive others",
        "Do not use our services for harassment or hate speech",
        "Do not attempt to reverse engineer our AI models",
        "Do not overload our systems with excessive API requests"
      ]
    },
    {
      title: "Intellectual Property",
      content: [
        "You retain ownership of all audio content you upload to our platform. By using our services, you grant us a limited license to process your content as necessary to provide our services.",
        "All AI models, algorithms, and platform features remain the exclusive property of SoundFlowAI. You may not copy, modify, or distribute any part of our technology."
      ]
    },
    {
      title: "Payment Terms",
      content: [
        "Subscription fees are billed in advance on a monthly or annual basis. All fees are non-refundable except as required by law. We reserve the right to change pricing with 30 days notice."
      ],
      bullets: [
        "Payments are processed securely through our payment partners",
        "Failed payments may result in service suspension",
        "Annual subscriptions receive a 20% discount",
        "Enterprise pricing is available for large-scale deployments"
      ]
    },
    {
      title: "Limitation of Liability",
      content: [
        "SoundFlowAI shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services. Our total liability shall not exceed the amount paid by you in the preceding 12 months."
      ]
    },
    {
      title: "Termination",
      content: [
        "We may terminate or suspend your account at any time for violations of these Terms. Upon termination, your right to use our services ceases immediately. You may request a copy of your data within 30 days of termination."
      ]
    }
  ];

  const faqs = [
    {
      question: "Can I use generated audio for commercial purposes?",
      answer: "Yes, audio generated through our platform can be used for commercial purposes, subject to the licensing terms of your subscription plan. Pro and Enterprise plans include full commercial rights."
    },
    {
      question: "What happens if I exceed my usage limits?",
      answer: "If you exceed your plan's usage limits, you will be notified and given the option to upgrade or purchase additional credits. Services may be throttled if limits are significantly exceeded."
    },
    {
      question: "Can I cancel my subscription at any time?",
      answer: "Yes, you can cancel your subscription at any time through your account settings. Your subscription will remain active until the end of the current billing period."
    },
    {
      question: "Do you offer refunds?",
      answer: "We offer a 14-day money-back guarantee for new subscribers. After this period, refunds are evaluated on a case-by-case basis and are generally not provided for partial months."
    }
  ];

  const navigate = useNavigate();

  return (
    <>
      {/* Back to Home Button */}
      <div className="fixed top-4 left-4 z-50">
        <motion.button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-neon-orange/10 border border-neon-orange/30 text-neon-orange hover:bg-neon-orange/20 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Back to home"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="text-sm font-medium">Home</span>
        </motion.button>
      </div>

      <PolicyPageLayout
        title="Terms of Service"
        lastUpdated="March 1, 2026"
        sections={sections}
        faqs={faqs}
      />
    </>
  );
};

export default TermsOfService;
